import { selectSellPriceData } from '../../redux/slices/sellPriceSlice';
import { getScheduleLocation } from '../../utils/shipToLocation';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Row, Col, Form, Accordion } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { updateJob, fetchJobSchedule } from '../../redux/slices/jobScheduleSlice';
import { patchData } from '../../redux/slices/apiCallSlice';
import { selectSchedulePumpDetails } from "../../redux/selectors";
import SaveBanner from '../JobDetailPage/Mechanical/SaveBanner';
import EngineerAssignmentModal from './EngineerAssignmentModal';
import ScheduleNotificationModal from './ScheduleNotificationModal';

import useScheduleChangeLog from '../../functions/useScheduleChangeLog';
import TbdNotice from './TbdNotice';
import WitnessTestNotice from './WitnessTestNotice';
import LongBuildNotice from './LongBuildNotice';
import AlertsModal from './AlertsModal';
// Import centralized date utilities
import { 
  formatDateUnified,
  formatDateForForm,
  isValidDateFormat
} from '../../utils/dateUtils';
// Import permission utilities
import { 
  getEditableFields,
  isFieldEditable // <-- import the new helper
} from './utils/schedulePermissions';

// Helper function for text processing
const separateCamelCase = (text) => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Separate camel case
    .replace(/(HC|VT|NP|CSM|HDPE)/g, ' $1 ') // Treat specific abbreviations as single words
    .replace(/\s+/g, ' ') // Remove extra spaces
    .trim();
};

const ScheduleJobModal = ({ selectedJob, setSelectedJob, handleCloseModal, handleSaveChanges }) => {
  // Fix: Properly access the apiUrls from the nested structure in the Redux store
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  const dispatch = useDispatch();
  const soldToShipToByProject = useSelector(selectSellPriceData);
  const options = useSelector((state) => state.dropdown.options); // Get dropdown options from Redux    // Get user dropdowns from Redux store
  const userDropdowns = useSelector((state) => state.dropdown.userDropdowns);
  
  // Get user roles from Redux store
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  
  // Get pump details from Redux store for notices
  const pumpDetails = useSelector(selectSchedulePumpDetails);
  
  const mechOptions = userDropdowns.mechOptions || [];
  const elecOptions = userDropdowns.elecOptions || [];
  const controlOptions = userDropdowns.controlOptions || []; // Add controlOptions
  const salesOptions = userDropdowns.salesOptions || []; // Add sales options
  
  // Get schedule change logging functionality
  const { logScheduleChange } = useScheduleChangeLog();

  // SaveBanner state for notifications
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  
  // State for date input modes
  const [formDateModes, setFormDateModes] = useState({});
  // State for engineer assignment modal
  const [showEngineerModal, setShowEngineerModal] = useState(false);  // State to track if changes were made
  const [changesMade, setChangesMade] = useState(false);
  
  // State to track original job data for change logging
  const [originalJobData, setOriginalJobData] = useState(null);
  
  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);  // State for schedule notification modal
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  
  // State for alerts modal
  const [showAlertsModal, setShowAlertsModal] = useState(false);

  // Capture original job data when modal opens for change tracking
  useEffect(() => {
    if (selectedJob && !originalJobData) {
      setOriginalJobData({ ...selectedJob });
    }
  }, [selectedJob, originalJobData]);

  // Format OrderDate and ProductionRelease dates when the component loads or selectedJob changes
  useEffect(() => {
    if (selectedJob) {
      // Create a new object only if we need to format the dates
      let needsFormatting = false;
      const formattedJob = { ...selectedJob };
        // Format OrderDate date
      if (formattedJob.OrderDate) {
        const formattedDate = formatDateUnified(formattedJob.OrderDate, 'input');
        if (formattedDate !== formattedJob.OrderDate) {
          formattedJob.OrderDate = formattedDate;
          needsFormatting = true;
        }
      }
      
      // Format ProductionRelease date
      if (formattedJob.ProductionRelease) {
        const formattedDate = formatDateUnified(formattedJob.ProductionRelease, 'input');
        if (formattedDate !== formattedJob.ProductionRelease) {
          formattedJob.ProductionRelease = formattedDate;
          needsFormatting = true;
        }
      }
        // Only update state if formatting was actually needed
      if (needsFormatting) {
        setSelectedJob(formattedJob);
      }
    }
  }, [selectedJob, setSelectedJob]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for DollarValue to prevent NaN formatting issues
    if (name === 'DollarValue') {
      // Remove any non-numeric characters except decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      setSelectedJob(prevJob => ({
        ...prevJob,
        [name]: numericValue
      }));
    }    // Special handling for OrderDate and ProductionRelease to ensure they don't have time components
    else if (name === 'OrderDate' || name === 'ProductionRelease') {
      setSelectedJob(prevJob => ({
        ...prevJob,
        [name]: formatDateUnified(value, 'input')
      }));
    }
    else {
      setSelectedJob(prevJob => ({
        ...prevJob,
        [name]: value
      }));
    }
      // Mark that changes have been made
    setChangesMade(true);
  };

  const buildPatchPayload = (originalData, newData) => {
    if (!originalData) {
      return { ...newData };
    }

    const patchPayload = { ID: newData.ID };
    const editableFieldsList = getEditableFields(userRolesMapped);
    const fieldsToCheck = editableFieldsList === 'all' ? Object.keys(newData) : editableFieldsList;

    fieldsToCheck.forEach((field) => {
      if (field === 'ID' || field === 'id') {
        return;
      }

      const originalValue = originalData[field] || '';
      const newValue = newData[field] || '';

      if (String(originalValue).trim() !== String(newValue).trim()) {
        patchPayload[field] = newData[field];
      }
    });

    if (!originalData?.ScheduledDate && newData.ScheduledDate) {
      patchPayload.ScheduledDate = newData.ScheduledDate;
    }

    return patchPayload;
  };

  // Helper function to detect and log changes
  const detectAndLogChanges = async (originalData, newData) => {
    const changedFields = [];
    
    // Get the fields that the user can edit using the mapped roles
    const editableFieldsList = getEditableFields(userRolesMapped);
    const fieldsToCheck = editableFieldsList === 'all' ? Object.keys(newData) : editableFieldsList;
    
    // Find all changed fields
    for (const field of fieldsToCheck) {
      const originalValue = originalData[field] || '';
      const newValue = newData[field] || '';
      
      // Convert values to strings for comparison to handle null/undefined/empty cases
      const originalStr = String(originalValue).trim();
      const newStr = String(newValue).trim();
      
      if (originalStr !== newStr) {
        changedFields.push({
          field,
          originalValue: originalValue,
          newValue: newValue
        });
      }
    }
    
    // Log each change sequentially
    for (const change of changedFields) {
      try {
        await logScheduleChange(
          newData.ID,
          newData.JobNumber || '',
          newData.ProjectName || '',
          change.field,
          change.originalValue,
          change.newValue
        );
        console.log(`Logged change for ${change.field}: "${change.originalValue}" -> "${change.newValue}"`);
      } catch (logError) {
        console.warn(`Failed to log change for field ${change.field}:`, logError);
        // Continue with other changes even if one fails
      }
    }
    
    return changedFields.length;
  };

  // Handle opening the engineer assignment modal
  const handleOpenEngineerModal = () => {
    setShowEngineerModal(true);
  };

  // Handle closing the engineer assignment modal
  const handleCloseEngineerModal = () => {
    setShowEngineerModal(false);
  };

  // Handle engineer selection from the modal
  const handleEngineerSelection = (engineerType, engineer, additionalInfo = {}) => {
    console.log('handleEngineerSelection called with:', { engineerType, engineer: engineer.displayName, additionalInfo });
    
    setSelectedJob(prevJob => {
      const updatedJob = {
        ...prevJob,
        [engineerType]: engineer.displayName
      };
      
      // Update due date if provided
      if (additionalInfo.dueDateField && additionalInfo.dueDate) {
        updatedJob[additionalInfo.dueDateField] = additionalInfo.dueDate;
        console.log(`Setting ${additionalInfo.dueDateField} to ${additionalInfo.dueDate}`);
      }
      
      // Update duration if provided
      if (additionalInfo.durationField && additionalInfo.duration) {
        updatedJob[additionalInfo.durationField] = additionalInfo.duration;
        console.log(`Setting ${additionalInfo.durationField} to ${additionalInfo.duration}`);
      }
      
      console.log('Updated job:', updatedJob);
      return updatedJob;
    });
    setShowEngineerModal(false);
    
    // Mark that changes have been made
    setChangesMade(true);
  };
    const handleScheduleSave = async () => {
    try {
      console.log('Starting job scheduling...');
      
      // Make sure we have original data for comparison
      if (!originalJobData) {
        console.error('No original job data available for change tracking');
        return;
      }

      // Make sure ID is included in the request body
      const jobWithID = { ...selectedJob };

      // Ensure ID exists in the request body, even if it was removed elsewhere
      if (!jobWithID.ID && selectedJob.ID) {
        jobWithID.ID = selectedJob.ID;
      }
      
      // Ensure boolean fields that cannot be null have default values
      const fieldsToDefaultFalse = ['SendDocumentsToSales', 'CustomControlsEngineering', 'NewConstruction', 'HasSubmittal'];
      fieldsToDefaultFalse.forEach(field => {
        if (jobWithID[field] === null || jobWithID[field] === undefined) {
          jobWithID[field] = false;
        }
      });
        // Format date fields to ensure they don't have time components
      if (jobWithID.OrderDate) {
        jobWithID.OrderDate = formatDateUnified(jobWithID.OrderDate, 'input');
      }
      
      if (jobWithID.ProductionRelease) {
        jobWithID.ProductionRelease = formatDateUnified(jobWithID.ProductionRelease, 'input');
      }
        // Skip internal UI state properties
      const filteredData = {};
      Object.keys(jobWithID).forEach(key => {
        // Skip internal state keys used for the UI
        if (!key.includes('_showAsText')) {
          filteredData[key] = jobWithID[key];
        }
      });
      
      // Add scheduled date if not present
      // Use consistent casing (ScheduledDate) to match the editableFields constant
      if (!filteredData.ScheduledDate) {
        filteredData.ScheduledDate = new Date().toISOString().split('T')[0];
      }
      
      // Make sure we don't have both camelCase and PascalCase versions of the same field
      if (filteredData.scheduledDate) {
        delete filteredData.scheduledDate; // Remove the camelCase version
      }

      const patchPayload = buildPatchPayload(originalJobData, filteredData);
      const changedFieldCount = Object.keys(patchPayload).filter((key) => key !== 'ID' && key !== 'id').length;

      if (changedFieldCount === 0) {
        setSaveStatus('No changes to save');
        setShowSaveBanner(true);
        setTimeout(() => {
          setShowSaveBanner(false);
        }, 2000);
        return;
      }
      
      // First: Make the main API call and wait for success
      console.log('Sending data to API:', patchPayload);
      const updateResponseData = await dispatch(
        patchData({
          url: `${apiUrls.generalProject}/?id=${filteredData.ID}`,
          data: patchPayload,
          baselineData: originalJobData,
        })
      ).unwrap();
      
      // Only proceed with logging if the main API call succeeded
      if (updateResponseData) {
        console.log('Job scheduled successfully:', updateResponseData);
        
        // Second: Log changes to the schedule change log (only if main update succeeded)
        try {
          const changeCount = await detectAndLogChanges(originalJobData, filteredData);
          console.log(`Successfully logged ${changeCount} changes`);
          
          // Additionally log the scheduling action if status changed
          if (originalJobData.JobStatus !== filteredData.JobStatus) {
            await logScheduleChange(
              filteredData.ID,
              filteredData.JobNumber || '',
              filteredData.ProjectName || '',
              'Scheduling Action',
              originalJobData.JobStatus || 'Unscheduled',
              `Job Status Updated to ${filteredData.JobStatus}`
            );
          }
        } catch (logError) {
          console.warn('Main update succeeded but some change logging failed:', logError);
          // Don't throw here - the main update worked
        }
        
        // Third: Update Redux store (only if main update succeeded)
        dispatch(updateJob(patchPayload));
        
        // Refresh the job schedule data to ensure grid shows updated information
        dispatch(fetchJobSchedule());

        // Show success notification
        setSaveStatus("Successfully scheduled job");
        setShowSaveBanner(true);
        
        // Hide the notification after 3 seconds
        setTimeout(() => {
          setShowSaveBanner(false);
        }, 3000);
        
        if (handleSaveChanges) {
          handleSaveChanges();
        }

        // Reset changes made flag since we've saved successfully
        setChangesMade(false);
        
        // Close the modal after successful scheduling
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error scheduling job:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      // Show error notification
      setSaveStatus(`Failed to schedule job: ${error.message}`);
      setShowSaveBanner(true);
      
      // Hide the notification after 5 seconds
      setTimeout(() => {
        setShowSaveBanner(false);
      }, 5000);
    }
  };

  const handleDateInputModeToggle = (field, isTextMode) => {
    setFormDateModes((prevModes) => ({
      ...prevModes,
      [field]: isTextMode
    }));
    
    // Mark that changes have been made
    setChangesMade(true);
  };

  // Handle confirmation for closing the modal
  const handleCloseWithConfirmation = () => {
    if (changesMade) {
      setShowConfirmationModal(true);
    } else {
      handleCloseModal();
    }
  };  // Handle confirmed close
  const handleConfirmClose = () => {
    setShowConfirmationModal(false);
    setChangesMade(false);
    handleCloseModal();
  };
  // Handle save with notification
  const handleSaveWithNotification = () => {
    setShowNotificationModal(true);
  };
  // Handle notification sent callback
  const handleNotificationSent = () => {
    // Perform the actual save after notification is sent
    handleScheduleSave();
  };

  // Handle save only (without notification)
  const handleSaveOnly = () => {
    // Perform the save without sending notification
    handleScheduleSave();
  };
  // Handle notification modal close
  const handleNotificationModalClose = () => {
    setShowNotificationModal(false);
  };



  // Function to handle alerts modal close
  const handleCloseAlertsModal = () => {
    setShowAlertsModal(false);
  };



  const renderDateField = (field, readOnly = false, showAssignButton = false, allowTextToggle = false) => {
    // Date field handling
    const dateValue = selectedJob[field] || '';
      // Format OrderDate and ProductionRelease dates specifically
    const formattedDate = (field === 'OrderDate' || field === 'ProductionRelease')
      ? formatDateUnified(dateValue, 'input')
      : formatDateUnified(dateValue, 'input');
      
    const isValidDate = isValidDateFormat(dateValue);
    const isTextMode = formDateModes[field] || false;

    // Use isFieldEditable for readOnly/disabled
    const editable = isFieldEditable(userRolesMapped, field, selectedJob);

    return (
      <Form.Group className="mb-3">
        <div className="d-flex align-items-center mb-2">
          <Form.Label className="mb-0">
            {separateCamelCase(field)}
          </Form.Label>
          {allowTextToggle && (
            <Form.Check
              type="checkbox"
              id={`${field}-text-mode`}
              className="ms-3"
              checked={isTextMode}
              onChange={(e) => handleDateInputModeToggle(field, e.target.checked)}
            />
          )}
        </div>
        <div className="d-flex">
          {isTextMode || !isValidDate ? (
            <Form.Control
              type="text"
              id={field}
              name={field}
              value={dateValue}
              onChange={handleInputChange}
              placeholder="Enter date as text"
              className={showAssignButton ? "flex-grow-1 me-2" : "w-100"}
              readOnly={!editable}
              disabled={!editable}
            />
          ) : (
            <Form.Control
              type="date"
              id={field}
              name={field}
              value={formattedDate}
              onChange={handleInputChange}
              readOnly={!editable}
              disabled={!editable}
              className={showAssignButton ? "flex-grow-1 me-2" : "w-100"}
            />
          )}
          {showAssignButton && (
            <Button 
              variant="outline-primary" 
              onClick={handleOpenEngineerModal}
              title="Find and assign engineers"
            >
              <i className="bi bi-people-fill"></i> Find Engineers
            </Button>
          )}
        </div>
      </Form.Group>
    );
  };

  const renderField = (field, readOnly = false, isDate = false, allowTextToggle = false) => {
    // Use isFieldEditable for all fields
    const editable = isFieldEditable(userRolesMapped, field, selectedJob);
    const isFieldReadOnly = readOnly || !editable;

    if (!isDate) {
      // Check if this is the JobLocation field and provide a custom label
      if (field === 'JobLocation') {
        const location = getScheduleLocation(selectedJob, soldToShipToByProject);
        return (
          <Form.Group className="mb-3">
            <Form.Label>Job Location (City, State)</Form.Label>
            <Form.Control
              type="text"
              id={field}
              name={field}
              value={location.fromShipTo ? location.value : selectedJob[field] || ''}
              onChange={handleInputChange}
              readOnly={isFieldReadOnly || location.fromShipTo}
              disabled={isFieldReadOnly || location.fromShipTo}
            />
          </Form.Group>
        );
      }
      
      return (
        <Form.Group className="mb-3">
          <Form.Label>{separateCamelCase(field)}</Form.Label>
          <Form.Control
            type="text"
            id={field}
            name={field}
            value={selectedJob[field] || ''}
            onChange={handleInputChange}
            readOnly={isFieldReadOnly}
            disabled={isFieldReadOnly}
          />
        </Form.Group>
      );
    }

    return renderDateField(field, isFieldReadOnly, false, allowTextToggle);
  };

  const renderDropdownField = (field, options, displayNameField = null) => {
    const currentValue = selectedJob[field] || '';
    // Use isFieldEditable for dropdowns
    const editable = isFieldEditable(userRolesMapped, field, selectedJob);

    return (
      <Form.Group className="mb-3">
        <Form.Label>{separateCamelCase(field)}</Form.Label>
        <Form.Select
          id={field}
          name={field}
          value={currentValue}
          onChange={handleInputChange}
          disabled={!editable}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option 
              key={typeof option === 'string' ? option : option.ID || option.displayName} 
              value={typeof option === 'string' ? option : (displayNameField ? option[displayNameField] : option.DisplayText)}
            >
              {typeof option === 'string' ? option : (displayNameField ? option[displayNameField] : option.DisplayText)}
            </option>
          ))}
          {!isValueInDropdown(currentValue, options) && currentValue && (
            <option value={currentValue} style={{ color: 'red' }}>
              *{currentValue}
            </option>
          )}
        </Form.Select>
      </Form.Group>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    // Don't try to format while the user is typing - just return the raw value
    // This prevents NaN from appearing during input
    return value;
  };

  // Function to check if value exists in dropdown options
  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  return (
    <>
      {/* Render SaveBanner at the top of the page, outside the modal */}
      {showSaveBanner && <SaveBanner showNotification={showSaveBanner} saveStatus={saveStatus} />}
      <Modal.Header closeButton onHide={handleCloseWithConfirmation}>
        <Modal.Title>Schedule Job</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedJob && (          <Form>            {/* Add TBD Notice at the top of the form */}
            {selectedJob.ID && <TbdNotice jobId={selectedJob.ID} />}
            {/* Add Witness Test Notice */}
            {selectedJob && <WitnessTestNotice job={selectedJob} />}            {/* Add Long Build Notice */}
            {selectedJob && <LongBuildNotice job={selectedJob} pumpDetails={pumpDetails} />}
            
            <Row>
              <Col md={4}>{renderField('ID', true)}</Col>
              <Col md={4}>{renderDropdownField('JobStatus', 
                options.filter(option => option.DropdownType === 'Job Status')
              )}</Col>
              <Col md={4}>{renderDropdownField('JobType', 
                options.filter(option => option.DropdownType === 'Job Type')
              )}</Col>
            </Row>
            
            {/* Basic Job Information */}
            <h5 className="mt-4 mb-3 border-bottom pb-2">Basic Job Information</h5>
            <Row>
            <Col>{renderField('JobNumber', selectedJob.ScheduleOnly !== "Yes")}</Col>
            <Col>{renderField('ProjectName', selectedJob.ScheduleOnly !== "Yes")}</Col>
            <Col>{renderField('ModelNumber', selectedJob.ScheduleOnly !== "Yes")}</Col>
          </Row>            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Order Date</Form.Label>
                  <Form.Control
                    type="date"
                    id="OrderDate"
                    name="OrderDate"
                    value={formatDateForForm(selectedJob.OrderDate) || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col>{renderField('ProductionRelease', false, true)}</Col>
              <Col>{renderField('RequestedDeliveryDate', false, true)}</Col>
            </Row><Row>
              <Col>{renderDropdownField('SalesRep', salesOptions, 'displayName')}</Col>
              <Col>{renderField('JobLocation')}</Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Dollar Value</Form.Label>
                  <Form.Control
                    type="text"
                    id="DollarValue"
                    name="DollarValue"
                    value={formatCurrency(selectedJob.DollarValue)}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col>{renderField('PaymentTerms')}</Col>
            </Row>
            
            <Accordion alwaysOpen>
              {/* Engineering - Reorganized with Due Date first */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>Engineering</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('MechanicalDue', false, true)}</Col>
                    <Col>{renderField('ElectricalDue', false, true)}</Col>
                    <Col>{renderField('ControlsDue', false, true)}</Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mechanical Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min=".00"
                          id="MechanicalDuration"
                          name="MechanicalDuration"
                          value={selectedJob.MechanicalDuration || '1'}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Electrical Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min=".00"
                          id="ElectricalDuration"
                          name="ElectricalDuration"
                          value={selectedJob.ElectricalDuration || '1'}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Controls Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min=".00"
                          id="ControlsDuration"
                          name="ControlsDuration"
                          value={selectedJob.ControlsDuration || '1'}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mechanical Engineer</Form.Label>
                        <div className="d-flex">
                          <Form.Select
                            id="MechanicalEngineer"
                            name="MechanicalEngineer"
                            value={selectedJob.MechanicalEngineer || ''}
                            onChange={handleInputChange}
                            className="flex-grow-1 me-2"
                          >
                            <option value="">Select...</option>
                            {mechOptions.map((option) => (
                              <option key={option.displayName} value={option.displayName}>
                                {option.displayName}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Electrical Engineer</Form.Label>
                        <div className="d-flex">
                          <Form.Select
                            id="ElectricalEngineer"
                            name="ElectricalEngineer"
                            value={selectedJob.ElectricalEngineer || ''}
                            onChange={handleInputChange}
                            className="flex-grow-1 me-2"
                          >
                            <option value="">Select...</option>
                            {elecOptions.map((option) => (
                              <option key={option.displayName} value={option.displayName}>
                                {option.displayName}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Controls Engineer</Form.Label>
                        <div className="d-flex">
                          <Form.Select
                            id="ControlsEngineer"
                            name="ControlsEngineer"
                            value={selectedJob.ControlsEngineer || ''}
                            onChange={handleInputChange}
                            className="flex-grow-1 me-2"
                          >
                            <option value="">Select...</option>
                            {controlOptions.map((option) => (
                              <option key={option.displayName} value={option.displayName}>
                                {option.displayName}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Button 
                        variant="outline-primary" 
                        onClick={handleOpenEngineerModal}
                        className="w-100"
                      >
                        <i className="bi bi-people-fill"></i> Find Available Engineers
                      </Button>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Fabrication */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>Fabrication</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('NPPipeFabDue', false, true)}</Col>
                    <Col>{renderField('FusionBondRequired')}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('FabDue', false, true)}</Col>
                    <Col>{renderField('FabExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Control Panel */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>Control Panel</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('SoftwareDue', false, true)}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('ControlPanelDue', false, true)}</Col>
                    <Col>{renderField('ControlPanelExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Pumps */}
              <Accordion.Item eventKey="3">
                <Accordion.Header>Pumps</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('HCPumpsDue', false, true)}</Col>
                    <Col>{renderField('VTPumpsDue', false, true)}</Col>
                    <Col>{renderField('PumpExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Assembly */}
              <Accordion.Item eventKey="4">
                <Accordion.Header>Assembly</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('AssemblyDue', false, true)}</Col>
                    <Col>{renderField('AssemblyExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Test */}
              <Accordion.Item eventKey="5">
                <Accordion.Header>Test</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('TestDue', false, true)}</Col>
                    <Col>{renderField('TestExpectedCompletion', false, true, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* NPCSMHDPE */}
              <Accordion.Item eventKey="6">
                <Accordion.Header>NP CSM HDPE</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('NPCSMDue', false, true)}</Col>
                    <Col>{renderField('NPSuctionLineDue', false, true)}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('NPInducerTubeDue', false, true)}</Col>
                    <Col>{renderField('NPBMXDue', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Shipping */}
              <Accordion.Item eventKey="7">
                <Accordion.Header>Shipping</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('PackDue', false, true)}</Col>
                    <Col>{renderField('TransportType')}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('OriginalScheduledShipDate', false, true)}</Col>
                    <Col>{renderField('CurrentPlanShipDate', false, true)}</Col>
                    <Col>{renderField('ScheduledArrivalDate', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* Notes section outside accordion */}            {/* Schedule Information - Add ScheduledDate field */}
            
            <h5 className="mt-4 mb-3 border-bottom pb-2">Notes</h5>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{separateCamelCase('Notes')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    id="Notes"
                    name="Notes"
                    value={selectedJob.Notes || ''}
                    onChange={handleInputChange}
                    placeholder="Enter notes about this job scheduling"
                    style={{ resize: "vertical" }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseWithConfirmation}>
          Cancel
        </Button>        <Button variant="primary" onClick={handleSaveWithNotification}>
          Schedule Job
        </Button>
      </Modal.Footer>

      {/* Engineer Assignment Modal */}
      <EngineerAssignmentModal
        show={showEngineerModal}
        onHide={handleCloseEngineerModal}
        onSelectEngineer={handleEngineerSelection}
        engineeringDueDate={selectedJob?.EngineeringDue || ''}
        mechanicalDueDate={selectedJob?.MechanicalDue || ''}
        electricalDueDate={selectedJob?.ElectricalDue || ''}
        controlsDueDate={selectedJob?.ControlsDue || ''}
        mechanicalEngineers={mechOptions}
        electricalEngineers={elecOptions}
        controlsEngineers={controlOptions}
        selectedJob={selectedJob}
      />
        {/* Confirmation Modal */}
      <Modal className="job-schedule-modal" show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Are you sure you want to close without scheduling this job?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmClose}>
            Close Without Saving
          </Button>        </Modal.Footer>
      </Modal>      {/* Schedule Notification Modal */}
      <ScheduleNotificationModal
        show={showNotificationModal}
        onHide={handleNotificationModalClose}
        jobData={selectedJob}        onNotificationSent={handleNotificationSent}
        onSaveOnly={handleSaveOnly}
      />
      
      {/* Alerts Modal for non-admin users */}
      <AlertsModal
        show={showAlertsModal}
        onHide={handleCloseAlertsModal}
        job={selectedJob}
        pumpDetails={pumpDetails}
      />
    </>
  );
};

ScheduleJobModal.propTypes = {
  selectedJob: PropTypes.object,
  setSelectedJob: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  handleSaveChanges: PropTypes.func,
};

export default ScheduleJobModal;
